import { getTimezone } from '@/utils/reward-machine/get-timezone';
import { type SetState } from '../store';
import { convertUtcToLocalDate } from '@/utils/date/convert-utc-date';
import type { EntryData } from '@/types/reward-machine';
import { setLocalStorageRewardsMachineWelcome } from '@/utils/reward-machine/reward-machine-helper';

export type RewardMachineSliceProps = {
    rewardMachine: {
        welcomeModal: {
            opened: boolean;
            setOpened: () => void;
            /** Data passed to the welcome modal when opened (e.g. from banner or RM pages). Kept unknown to allow different page shapes. */
            welcomeModalData: unknown;
            setWelcomeModalData: (data: unknown) => void;
        };
        entry: EntryData | null;
        error: boolean;
        rmLoadingCompleted: boolean;
        entryFetchInFlight: boolean;
        entryLastSuccessAt: number;
        reauthBlocked: boolean;
        setEntryData: (entryData: EntryData) => void;
        setError: () => void;
        setLoadingCompleted: () => void;
        setEntryFetchInFlight: (inFlight: boolean) => void;
        setEntryLastSuccessAt: (ms: number) => void;
        setReauthBlocked: (blocked: boolean) => void;
        reset: () => void;
    };
};

const rewardMachineWelcomeDisplayed = (displayed: boolean): void => {
    setLocalStorageRewardsMachineWelcome(displayed);
};

const ACTIONS = {
    SET_ENTRY_DATA: 'rewardMachine/setEntryData',
    UPDATE_WELCOME: 'rewardMachine/updateWelcome',
    SET_ERROR: 'rewardMachine/setError',
    SET_LOADING_COMPLETED: 'rewardMachine/setLoadingCompleted',
    SET_ENTRY_FETCH_IN_FLIGHT: 'rewardMachine/setEntryFetchInFlight',
    SET_ENTRY_LAST_SUCCESS_AT: 'rewardMachine/setEntryLastSuccessAt',
    SET_REAUTH_BLOCKED: 'rewardMachine/setReauthBlocked',
    RESET: 'rewardMachine/reset'
};

const INITIAL_WELCOME_MODAL_STATE = {
    opened: false,
    welcomeModalData: null as unknown
};

const INITIAL_STATE = {
    welcomeModal: INITIAL_WELCOME_MODAL_STATE,
    entry: null,
    welcome: false,
    error: false,
    rmLoadingCompleted: false,
    entryFetchInFlight: false,
    entryLastSuccessAt: 0,
    reauthBlocked: false
};

export const rewardMachineSlice = ({ set }: SetState<RewardMachineSliceProps>): RewardMachineSliceProps => ({
    rewardMachine: {
        ...INITIAL_STATE,
        welcomeModal: {
            ...INITIAL_WELCOME_MODAL_STATE,
            setWelcomeModalData: (data: unknown) =>
                set(
                    (state) => ({
                        rewardMachine: {
                            ...state.rewardMachine,
                            welcomeModal: {
                                ...state.rewardMachine.welcomeModal,
                                welcomeModalData: data
                            }
                        }
                    }),
                    false,
                    'rewardMachine/welcomeModal/setWelcomeModalData'
                ),
            setOpened: () => {
                rewardMachineWelcomeDisplayed(true);

                set(
                    (state) => ({
                        rewardMachine: {
                            ...state.rewardMachine,
                            welcomeModal: {
                                ...state.rewardMachine.welcomeModal,
                                opened: true
                            }
                        }
                    }),
                    false,
                    ACTIONS.UPDATE_WELCOME
                );
            }
        },
        setEntryData: (entryData: EntryData) => {
            const processedEntryData = { ...entryData };

            if (processedEntryData.bonusDraw) {
                processedEntryData.bonusDraw = {
                    ...processedEntryData.bonusDraw,
                    closeTime: convertUtcToLocalDate(
                        processedEntryData.bonusDraw.closeTime,
                        getTimezone('MI')
                    ).getTime()
                };
            }
            set(
                (state) => ({
                    rewardMachine: {
                        ...state.rewardMachine,
                        entry: processedEntryData,
                        error: false
                    }
                }),
                false,
                ACTIONS.SET_ENTRY_DATA
            );
        },
        setError: () => {
            set(
                (state) => ({
                    rewardMachine: {
                        ...state.rewardMachine,
                        entry: null,
                        error: true
                    }
                }),
                false,
                ACTIONS.SET_ERROR
            );
        },
        setLoadingCompleted: () => {
            set(
                (state) => ({
                    rewardMachine: {
                        ...state.rewardMachine,
                        rmLoadingCompleted: true
                    }
                }),
                false,
                ACTIONS.SET_LOADING_COMPLETED
            );
        },
        setEntryFetchInFlight: (inFlight: boolean) => {
            set(
                (state) => ({
                    rewardMachine: {
                        ...state.rewardMachine,
                        entryFetchInFlight: inFlight
                    }
                }),
                false,
                ACTIONS.SET_ENTRY_FETCH_IN_FLIGHT
            );
        },
        setEntryLastSuccessAt: (ms: number) => {
            set(
                (state) => ({
                    rewardMachine: {
                        ...state.rewardMachine,
                        entryLastSuccessAt: ms
                    }
                }),
                false,
                ACTIONS.SET_ENTRY_LAST_SUCCESS_AT
            );
        },
        setReauthBlocked: (blocked: boolean) => {
            set(
                (state) => ({
                    rewardMachine: {
                        ...state.rewardMachine,
                        reauthBlocked: blocked
                    }
                }),
                false,
                ACTIONS.SET_REAUTH_BLOCKED
            );
        },

        reset: () => {
            set(
                (state) => ({
                    rewardMachine: {
                        ...state.rewardMachine,
                        ...INITIAL_STATE,
                        welcomeModal: {
                            ...INITIAL_WELCOME_MODAL_STATE,
                            setWelcomeModalData: state.rewardMachine.welcomeModal.setWelcomeModalData,
                            setOpened: state.rewardMachine.welcomeModal.setOpened
                        }
                    }
                }),
                false,
                ACTIONS.RESET
            );
        }
    }
});
